import React, { useMemo } from 'react';
import { useToken } from '@gluestack-ui/themed';
import escape from 'lodash/escape';

import HtmlText from '@/common/components/HtmlText';
import { useTenantGradingSettings } from '@/common/context/TenantSettingsStore';
import type { HighlightSegment } from '@/common/api/models/highlightSegment';

export const highlightQuotes = (
  text: string,
  highlightConfig: Record<string, HighlightSegment[]>,
  dimHexColors: Record<string, string>,
) => {
  let highlightedText = text;
  const dimensions = Object.keys(highlightConfig);

  if (!text || !highlightConfig || Object.keys(highlightConfig).length === 0) {
    return text;
  }

  dimensions.forEach((dimension) => {
    const segments = highlightConfig[dimension];
    const bgColor = dimHexColors[dimension] || '#f0f0f0'; // Default light gray if color not found
    if (segments && Array.isArray(segments) && segments.length > 0) {
      segments.forEach((segment) => {
        if (segment?.start && segment?.end) {
          const { start, end } = segment;
          const escapedStart = escape(start);
          const escapedEnd = escape(end);
          const startIndex = highlightedText.indexOf(escapedStart);
          const endIndex = highlightedText.indexOf(escapedEnd, startIndex);

          if (startIndex !== -1 && endIndex !== -1) {
            const actualEndIndex = endIndex + escapedEnd.length;
            const quote = highlightedText.slice(startIndex, actualEndIndex);
            highlightedText = highlightedText.replace(
              quote,
              `<span style="background-color: ${bgColor}">${quote}</span>`,
            );
          }
        }
      });
    }
  });

  return highlightedText;
};

interface HighlightedSubmissionTextProps {
  text: string;
  highlightConfig: Record<string, HighlightSegment[]> | null;
}

const HighlightedSubmissionText: React.FC<HighlightedSubmissionTextProps> = ({ text, highlightConfig }) => {
  const { evalDimensionKeys } = useTenantGradingSettings();

  // Resolve all possible dim tokens to avoid hook rule violations
  // Using 200 shade for a balanced highlight color
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dim0 = useToken('colors', 'dim0200' as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dim1 = useToken('colors', 'dim1200' as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dim2 = useToken('colors', 'dim2200' as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dim3 = useToken('colors', 'dim3200' as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dim4 = useToken('colors', 'dim4200' as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dim5 = useToken('colors', 'dim5200' as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dim6 = useToken('colors', 'dim6200' as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dim7 = useToken('colors', 'dim7200' as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dim8 = useToken('colors', 'dim8200' as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dim9 = useToken('colors', 'dim9200' as any);

  const dimTokens = useMemo(() => [dim0, dim1, dim2, dim3, dim4, dim5, dim6, dim7, dim8, dim9], [
    dim0, dim1, dim2, dim3, dim4, dim5, dim6, dim7, dim8, dim9,
  ]);

  // Map eval dimensions to their resolved colors
  const dimHexColors = useMemo(() => {
    const colors: Record<string, string> = {};
    evalDimensionKeys.forEach((dim, index) => {
      colors[dim] = dimTokens[index % 10];
    });
    return colors;
  }, [evalDimensionKeys, dimTokens]);

  // Pass resolved colors to utility function
  const highlightedText = highlightConfig ? highlightQuotes(text, highlightConfig, dimHexColors) : text;

  return <HtmlText content={highlightedText} applySubmissionFormatting />;
};

export default HighlightedSubmissionText;
